/**
 * PR 9.2h W4(Issue 2): 驱动词云 —— echarts-wordcloud。
 *
 * 复用 /api/v2/brands/{id}/drivers/wordcloud(weekly_brand_metrics 正/负驱动 +
 * fact 提及频率)。正面绿 / 负面红;100% 宽 + mobile 容器 scroll-x(9.2f sustain);
 * 无数据 placeholder。
 */
import { useQuery } from '@tanstack/react-query';
import { Empty, Skeleton } from 'antd';
import ReactECharts from 'echarts-for-react';
import 'echarts-wordcloud'; // 注册 wordCloud series 类型

import { api } from '@/lib/api';

interface WordcloudItem {
  keyword: string;
  frequency: number;
  polarity: 'positive' | 'negative';
}

interface DriverWordcloudProps {
  brandId: string;
  week: string;
  height?: number;
}

export default function DriverWordcloud({ brandId, week, height = 280 }: DriverWordcloudProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['driver-wordcloud', brandId, week],
    queryFn: async () =>
      (await api.get<WordcloudItem[]>(`/brands/${brandId}/drivers/wordcloud`, { params: { week } }))
        .data,
    retry: false,
    enabled: Boolean(brandId && week),
  });

  if (isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Empty description="本周暂无驱动词数据" />
      </div>
    );
  }

  const option = {
    tooltip: { show: true, confine: true },
    series: [
      {
        type: 'wordCloud',
        shape: 'circle',
        width: '100%',
        height: '100%',
        sizeRange: [14, 48],
        rotationRange: [0, 0],
        gridSize: 8,
        drawOutOfBound: false,
        textStyle: {
          fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
          color: (params: { data: WordcloudItem }) =>
            params.data.polarity === 'positive' ? '#15803D' : '#B91C1C',
        },
        data: data.map((d) => ({
          name: d.keyword,
          value: d.frequency,
          polarity: d.polarity,
        })),
      },
    ],
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[280px]">
        <ReactECharts option={option} style={{ height }} notMerge lazyUpdate />
      </div>
    </div>
  );
}
